/**
 * The next league fight on Home (designs/Caged Home Signed In, D50): the fight time, a countdown
 * to the second, the matchup, the game plan and reminders (D56), and Fight now once the live window
 * opens. Only for a player with a place in a league; the league data comes from state/league (Home
 * loads it).
 */
import { LEAGUES } from '@caged/shared';
import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLeague } from '../../state/league';
import { useApp } from '../../state/store';
import { useMyLook, useSecondNow } from '../hooks';
import { fightPhase, Flag, todayFixture, useClockTime, useFightCountdown, useResultLabel, useSeriesTitle } from '../league';
import { FighterArt } from './FighterArt';
import { ReminderPrompt } from './Reminders';
import ui from './ui.module.css';
import s from './LeagueFightCard.module.css';

/** The countdown pulses in its last hour. */
const SOON_MS = 60 * 60 * 1000;
/** While the window is open or results are due, the card asks again this often (ms). */
const REFRESH_MS = 20_000;

/** Whether the card shows: a player with a league place and a league day ahead or on. */
export function useLeagueFightShown(): boolean {
  const mine = useLeague((st) => st.mine);
  return mine?.status === 'ok' && mine.next.day > 0;
}

/**
 * `rise`: false when the card takes the place of its placeholder, which already held its spot (no
 * entrance then).
 */
export function LeagueFightCard({ size = 'md', rise = true }: { size?: 'md' | 'lg'; rise?: boolean }) {
  const { t } = useTranslation();
  const mine = useLeague((st) => st.mine);
  const load = useLeague((st) => st.load);
  const openLeague = useApp((st) => st.openLeague);
  const startLeagueFight = useApp((st) => st.startLeagueFight);
  const title = useSeriesTitle();
  const time = useClockTime();
  const countdown = useFightCountdown();
  const result = useResultLabel();
  const myLook = useMyLook();
  const now = useSecondNow();
  const view = mine?.status === 'ok' ? mine : null;
  const phase = view ? fightPhase(view.next, now) : 'none';

  // As the window opens and closes: who's waiting in the cage, then the result and the next day.
  useEffect(() => {
    if (phase !== 'live' && phase !== 'closing') return;
    void load(true);
    const id = setInterval(() => void load(true), REFRESH_MS);
    return () => clearInterval(id);
  }, [phase, load]);

  if (!view || phase === 'none') return null;
  const { next } = view;
  const over = view.daysDone >= LEAGUES.seasonDays;
  const { fixture, later, meSide, opp, me, oppRow } = todayFixture(view, view.me.slot, view.me.joinedDay);
  const waiting = view.me.opponentWaiting;
  const left = next.fightAt - now;
  const day = next.day === LEAGUES.qualifierDay ? t('screens.league.qualifierFight') : t('screens.league.dayN', { day: next.day });
  const label = `${t('screens.nav.league')} · ${day}`;

  let body: ReactNode;
  if (over) body = <p className={s.note}>{t('screens.league.seasonOver')}</p>;
  else if (later) body = <p className={s.note}>{t('screens.league.joinedLate')}</p>;
  else if (!fixture) body = <p className={s.note}>{t('screens.league.restDay')}</p>;
  else if (fixture.state === 'done') {
    const outcome = fixture.winner === null ? 'draw' : fixture.winner === meSide ? 'won' : 'lost';
    body = (
      <p className={[s.note, outcome === 'won' ? s.green : outcome === 'lost' ? s.red : ''].join(' ')}>
        {t(`screens.league.outcome.${outcome}`)} · {result(fixture)}
      </p>
    );
  } else if (phase === 'live' && (fixture.state === 'scheduled' || waiting)) {
    // The live window (L3): fight now, live if the opponent comes, else against their game plan.
    body = (
      <>
        <p className={[s.note, s.liveNote].join(' ')}>
          <span className={s.dot} aria-hidden="true" />
          {waiting
            ? t('screens.league.waitingForYou', { name: opp?.name ?? '' })
            : t('screens.league.liveNow', { time: countdown(next.windowEndsAt - now) })}
        </p>
        <button type="button" className={[ui.primary, s.fightNow].join(' ')} onClick={() => startLeagueFight(fixture.id)}>
          {t('screens.league.fightNow')}
          <span className={ui.arrow} aria-hidden="true">
            ▷
          </span>
        </button>
      </>
    );
  } else if (phase === 'live') body = <p className={[s.note, s.liveNote].join(' ')}>{t('screens.league.fightingNow')}</p>;
  else if (phase === 'closing') body = <p className={s.note}>{t('screens.league.resultsSoon')}</p>;
  else
    body = (
      <>
        <button type="button" className={[ui.secondary, s.planBtn].join(' ')} onClick={() => openLeague('plan')}>
          <span className={s.planDot} aria-hidden="true" />
          <span className={s.planText}>{t('screens.league.setPlan')}</span>
          <span className={ui.arrow} aria-hidden="true">
            →
          </span>
        </button>
        {/* Reminders, so nobody misses the fight without finding Settings (D56). */}
        <ReminderPrompt fightAt={next.fightAt} />
      </>
    );

  return (
    <section
      className={[s.card, size === 'lg' ? s.lg : '', phase === 'live' && fixture ? s.live : '', rise ? '' : s.still].join(' ')}
      aria-label={label}
    >
      <button type="button" className={s.head} onClick={() => openLeague('league')}>
        <span className={s.headTop}>
          <span className={ui.label}>{label}</span>
          <span className={s.headArrow} aria-hidden="true">
            →
          </span>
        </span>
        <span className={s.series}>
          <Flag country={view.country} />
          <span className={s.seriesName}>{title(view.series)}</span>
        </span>
      </button>

      {phase === 'upcoming' && !over && fixture && (
        <div className={[s.clock, left < SOON_MS ? s.soon : ''].join(' ')}>
          <div className={s.clockMain}>
            <span className={ui.label}>{t('screens.league.startsIn')}</span>
            <span className={s.countdown}>{countdown(left)}</span>
          </div>
          <span className={s.at}>{t('screens.league.fightAt', { time: time(next.fightAt) })}</span>
        </div>
      )}

      {fixture && opp && me && (
        <div className={s.versus}>
          <div className={s.side}>
            <FighterArt id={me.fighterId} height={size === 'lg' ? 84 : 52} look={myLook} />
            <span className={s.sideText}>
              <span className={s.name}>{t('screens.league.you')}</span>
              <span className={[s.meta, s.amber].join(' ')}>{t('screens.league.posPts', { pos: me.position, pts: me.points })}</span>
            </span>
          </div>
          <span className={s.vs}>{t('screens.league.vs')}</span>
          <div className={[s.side, s.sideOpp].join(' ')}>
            <FighterArt id={opp.fighterId} height={size === 'lg' ? 84 : 52} flip delay={0.4} />
            <span className={s.sideText}>
              <span className={s.name} data-name="">
                {opp.name}
              </span>
              <span className={[s.meta, s.green].join(' ')}>
                {oppRow ? t('screens.league.posPts', { pos: oppRow.position, pts: oppRow.points }) : (opp.series ?? '')}
              </span>
            </span>
          </div>
        </div>
      )}

      {body}
    </section>
  );
}

/**
 * The card's spot while the league loads, where the card showed last time (ui/bootHint.ts): the
 * same shape, so Home doesn't move when the card comes.
 */
export function LeagueFightCardPlaceholder({ size = 'md' }: { size?: 'md' | 'lg' }) {
  return (
    <div className={[s.card, s.still, size === 'lg' ? s.lg : ''].join(' ')} aria-hidden="true">
      <div className={s.boneHead}>
        <span className={[ui.bone, s.boneLabel].join(' ')} />
        <span className={[ui.bone, s.boneSeries].join(' ')} />
      </div>
      <div className={s.clock}>
        <div className={s.clockMain}>
          <span className={[ui.bone, s.boneLabel].join(' ')} />
          <span className={[ui.bone, s.boneCountdown].join(' ')} />
        </div>
        <span className={[ui.bone, s.boneAt].join(' ')} />
      </div>
      <div className={s.versus}>
        <div className={s.side}>
          <span className={[ui.bone, s.boneFighter].join(' ')} />
          <span className={[ui.bone, s.boneName].join(' ')} />
        </div>
        <span />
        <div className={[s.side, s.sideOpp].join(' ')}>
          <span className={[ui.bone, s.boneFighter].join(' ')} />
          <span className={[ui.bone, s.boneName].join(' ')} />
        </div>
      </div>
      <span className={[ui.bone, s.boneButton].join(' ')} />
      <span className={[ui.bone, s.boneRemind].join(' ')} />
    </div>
  );
}
